interface CurrencyFormatOptions {
  symbolAtEnd?: boolean;
  useSymbol?: boolean;
  decimalPlaces?: number;
}

export interface FormatCurrencyParams {
  amount: number;
  currencyCode: string;
  locale: string;
  options: CurrencyFormatOptions;
  featureName: string;
}

export interface FormattedCurrency {
  stringValue: string;
  currencySymbol: string;
  currencyCode: string;
  placement: 'start' | 'end';
  decimalPlaces: number;
  numberValue: number;
}

/**
 * @function formatCurrency - formatCurrency to use to format currency
 * @param {FormatCurrencyParams} configData {featureName="undefined",locale = "en-US", symbolAtEnd = false, decimalPlaces = 2, roundingValues = ["00", "0.25", "0.50", "0.75", "1"]} - Required parameters
 * @returns {FormattedCurrency} - FormattedCurrency
 * @example formatCurrency({amount: 1234.56, currencyCode:'THB', locale: 'th-TH', options: {useSymbol: true}, featureName: "test"});
 * @example formatCurrency({amount: 1234.56, currencyCode:'USD', locale: 'en-US', options: {useSymbol: true, symbolAtEnd: true}, featureName: "test"});
 * @example formatCurrency({amount: 1234.56, currencyCode:'EUR', locale: 'de-DE', options: {useSymbol: false,decimalPlaces: 2, roundingValues: ['0', '0.1', '0.2', '0.3', '0.4', '0.5', '0.6', '0.7', '0.8', '0.9', '1']}, featureName: "test"});
 */
export function formatCurrency(configData: Partial<FormatCurrencyParams>): FormattedCurrency {
  const {
    amount = 0,
    currencyCode = 'THB',
    locale = 'th-TH',
    options = {
      useSymbol: configData?.options?.useSymbol ?? true,
      decimalPlaces: configData?.options?.decimalPlaces ?? 2,
      symbolAtEnd: configData?.options?.symbolAtEnd ?? true
    }
  } = configData;

  const {symbolAtEnd = true, useSymbol = true, decimalPlaces = 2} = options;

  // Round the amount to the specified number of decimal places
  const roundedAmount = Number(amount.toFixed(decimalPlaces));

  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
    currencyDisplay: useSymbol ? 'symbol' : 'code'
  });

  const parts = formatter.formatToParts(roundedAmount);
  const currencySymbol = parts.find((part) => part.type === 'currency')?.value || currencyCode;
  const valueWithoutSymbol = parts
    .filter((part) => part.type !== 'currency')
    .map((part) => part.value)
    .join('')
    .trim();

  // Construct final stringValue according to the placement
  const stringValue = symbolAtEnd
    ? `${valueWithoutSymbol} ${currencySymbol}`
    : `${currencySymbol} ${valueWithoutSymbol}`;

  const val: FormattedCurrency = {
    stringValue,
    currencySymbol,
    currencyCode,
    placement: symbolAtEnd ? 'end' : 'start',
    decimalPlaces,
    numberValue: roundedAmount
  };

  return val;
}
